import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../auth";
import api from "../api";

const MONTHS = ["January","February","March","April","May","June",
                "July","August","September","October","November","December"];

export default function Attendance() {
  const { me, has } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const now = new Date();
  const [year,    setYear]    = useState(now.getFullYear());
  const [month,   setMonth]   = useState(now.getMonth());
  const [view,    setView]    = useState("calendar");
  const [days,    setDays]    = useState({});
  const [loading, setLoading] = useState(false);
  const [selected,setSelected]= useState(null);
  const [empList, setEmpList] = useState([]);
  const [selEmp,  setSelEmp]  = useState(searchParams.get("emp") || "");
  const isAdmin = me?.role === "admin" || me?.role === "hr" || me?.role === "manager";

  const monthStr = `${year}-${String(month+1).padStart(2,"0")}`;

  // Load employee list for admin
  useEffect(() => {
    if (!isAdmin) return;
    api.get("/portal/team").then(d => setEmpList(d.members||[])).catch(()=>{});
  }, [isAdmin]);

  // Sync URL param to state
  useEffect(() => {
    const emp = searchParams.get("emp");
    if (emp) setSelEmp(emp);
  }, [searchParams]);

  useEffect(() => {
    setLoading(true);
    const empParam = selEmp && selEmp !== me?.employee_id ? `&emp=${selEmp}` : "";
    api.get(`/portal/attendance?month=${monthStr}${empParam}`)
      .then(d => setDays(d.days||{}))
      .catch(()=>{})
      .finally(()=>setLoading(false));
  }, [monthStr, selEmp]);

  const nav = delta => {
    let m=month+delta, y=year;
    if(m<0){m=11;y--;} if(m>11){m=0;y++;}
    setMonth(m); setYear(y);
  };

  const statusPill = s => {
    const map={present:"green",late:"orange",half_day:"orange",absent:"red",holiday:"blue",leave:"purple"};
    return <span className={`pill ${map[s]||"gray"}`}>{s?.replace("_"," ")}</span>;
  };

  const today  = now.toISOString().slice(0,10);
  const selDay = selected && days[selected];
  const firstDow = new Date(year,month,1).getDay();
  const lastDay  = new Date(year,month+1,0).getDate();
  const DN = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

  const dayStyle = (ds, dow) => {
    const isWE = dow===0||dow===6;
    if (isWE) return {bg:"#F8FAFC",color:"var(--muted)"};
    const d = days[ds];
    if (!d) return ds>today?{bg:"var(--soft)"}:{bg:"#FEE2E2",color:"#991B1B"};
    const s = d.status;
    if (s==="present"||(s==="late"&&(d.total_hours>=8))) return {bg:"#DCFCE7",color:"#166534"};
    if (s==="half_day"||d.total_hours>0) return {bg:"#FEF3C7",color:"#92400e"};
    return {bg:"#FEE2E2",color:"#991B1B"};
  };

  const displayName = selEmp && selEmp !== me?.employee_id
    ? empList.find(e=>e.employee_id===selEmp)?.name || selEmp
    : `Myself (${me?.employee_id})`;

  return (
    <div>
      <div style={{marginBottom:4}}>
        <h2 className="page-title">Attendance</h2>
        <p className="page-sub">Your monthly attendance records.</p>
      </div>

      {/* Employee selector (admin only) */}
      {isAdmin && (
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:16}}>
          <label style={{fontSize:13,color:"var(--muted)",margin:0}}>Employee:</label>
          <select className="inp" style={{width:"auto",minWidth:200}} value={selEmp}
            onChange={e=>{setSelEmp(e.target.value); setSearchParams(e.target.value?{emp:e.target.value}:{});}}>
            <option value="">Myself ({me?.employee_id})</option>
            {empList.map(e=><option key={e.employee_id} value={e.employee_id}>{e.name} ({e.employee_id})</option>)}
          </select>
        </div>
      )}

      {/* Month nav with view toggle */}
      <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:20,flexWrap:"wrap"}}>
        <button onClick={()=>nav(-1)} className="btn secondary"
          style={{padding:"6px 14px",borderRadius:9,fontSize:18,width:38,height:38}}>‹</button>
        <b style={{fontSize:18,fontWeight:600,minWidth:165,textAlign:"center"}}>{MONTHS[month]} {year}</b>
        <button onClick={()=>nav(1)}  className="btn secondary"
          style={{padding:"6px 14px",borderRadius:9,fontSize:18,width:38,height:38}}>›</button>
        <div className="seg-ctrl" style={{marginLeft:"auto"}}>
          {[["calendar","Calendar"],["table","Table"]].map(([v,l])=>(
            <button key={v} className={`seg-btn ${view===v?"active":""}`} onClick={()=>setView(v)}>{l}</button>
          ))}
        </div>
      </div>

      {loading?<div className="loading">Loading…</div>:(
        <div className="card">
          {view==="calendar"&&(
            <>
              <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:4,marginBottom:8}}>
                {DN.map(d=><div key={d} style={{textAlign:"center",fontSize:11.5,fontWeight:600,
                color:"var(--muted)",padding:"6px 0",letterSpacing:".02em",
                textTransform:"uppercase"}}>{d}</div>)}
              </div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:4}}>
                {Array(firstDow).fill(null).map((_,i)=><div key={`e${i}`}/>)}
                {Array(lastDay).fill(0).map((_,i)=>{
                  const day=i+1;
                  const ds=`${year}-${String(month+1).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
                  const dow=(firstDow+i)%7;
                  const {bg,color}=dayStyle(ds,dow);
                  const d=days[ds];
                  return(
                    <div key={day}
                      className={`cal-day ${ds===today?"today":""}`}
                      onClick={()=>setSelected(ds===selected?null:ds)}
                      style={{background:bg,color:color||"inherit",
                        border:`1px solid ${ds===today?"var(--accent)":"transparent"}`}}>
                      <div className="day-num">{day}</div>
                      {d?.status&&d.status!=="absent"&&
                        <div className="day-status">{d.status}</div>}
                      {d?.total_hours&&d.total_hours!=="-"&&
                        <div className="day-h">{d.total_hours}h</div>}
                      {d?.holiday&&<div className="day-h">{d.holiday}</div>}
                    </div>
                  );
                })}
              </div>
              {selected&&(
                <div style={{marginTop:16,padding:16,background:"var(--soft)",borderRadius:8,fontSize:13}}>
                  <b style={{display:"block",marginBottom:10}}>{selected}</b>
                  {selDay?(<div style={{display:"grid",gap:6}}>
                    {[["Status",statusPill(selDay.status)],["First In",selDay.first_in],
                      ["Last Out",selDay.last_out],["Total Hours",selDay.total_hours],
                      ["Source",selDay.source]].map(([k,v])=>(
                      <div key={k} style={{display:"flex",justifyContent:"space-between"}}>
                        <span style={{color:"var(--muted)"}}>{k}</span><span style={{fontWeight:500}}>{v}</span>
                      </div>
                    ))}
                  </div>):<span style={{color:"var(--muted)"}}>No record for this day.</span>}
                </div>
              )}
              <div style={{display:"flex",gap:12,marginTop:14,fontSize:12,color:"var(--muted)",flexWrap:"wrap"}}>
                {[["#DCFCE7","≥ 8h"],["#FEF3C7","Partial/Late"],["#FEE2E2","Missing"],["#F8FAFC","Weekend"]].map(([bg,l])=>(
                  <span key={l} style={{display:"flex",alignItems:"center",gap:4}}>
                    <span style={{width:12,height:12,borderRadius:3,background:bg,display:"inline-block",border:"1px solid rgba(0,0,0,.08)"}}/>
                    {l}
                  </span>
                ))}
              </div>
            </>
          )}
          {view==="table"&&(
            <div style={{overflowX:"auto"}}>
              <table className="tbl">
                <thead><tr>{["Date","Status","First In","Last Out","Hours","Source"].map(h=><th key={h}>{h}</th>)}</tr></thead>
                <tbody>
                  {Object.entries(days).sort(([a],[b])=>a<b?-1:1).map(([ds,d])=>(
                    <tr key={ds}>
                      <td><b>{ds}</b></td>
                      <td>{statusPill(d.status)}</td>
                      <td>{d.first_in}</td><td>{d.last_out}</td>
                      <td>{d.total_hours!=="-"?`${d.total_hours}h`:"—"}</td>
                      <td style={{color:"var(--muted)",fontSize:12}}>{d.source}</td>
                    </tr>
                  ))}
                  {!Object.keys(days).length&&<tr><td colSpan={6} style={{textAlign:"center",color:"var(--muted)"}}>No records this month.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
